import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "../../styles/header.css";
import BASE_URL from "../../components/config/appconfig";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import usePageMeta from "../../utils/usePageMeta";

const ResetPassword = () => {
  usePageMeta("Reset Password", "Set a new password for your Gospel and Church Health Framework account.");
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState(null);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ FIX: useCallback added
  const redirectToLogin = useCallback(() => {
    setSnackbar({
      open: true,
      message: "Invalid or expired reset link",
      severity: "error",
    });

    setTimeout(() => navigate("/sign-in"), 2000);
  }, [navigate]);

  // ✅ FIX: dependency added
  useEffect(() => {
    const hash = window.location.hash;

    if (hash) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const access_token = params.get("access_token");

      if (access_token) {
        setAccessToken(access_token);
      } else {
        redirectToLogin();
      }
    } else {
      redirectToLogin();
    }
  }, [redirectToLogin]);

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const showError = (msg) => {
    setSnackbar({
      open: true,
      message: msg,
      severity: "error",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password || !formData.confirmPassword) {
      showError("All fields are required");
      return;
    }

    if (formData.password.length < 6) {
      showError("Password must be at least 6 characters");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showError("Passwords do not match");
      return;
    }

    if (!accessToken) {
      showError("Invalid session");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          password: formData.password,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: "Password reset successful",
          severity: "success",
        });

        setTimeout(() => navigate("/sign-in"), 2000);
      } else {
        showError(result.message);
      }
    } catch (err) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h3"
          className="title"
          sx={{
            fontSize: {
              xs: "20px !important",
              sm: "28px !important",
              md: "32px !important",
            },
            textAlign: "center",
          }}
        >
          The Gospel and Church Health Framework
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          maxWidth: 500,
          mx: "auto",
          p: { xs: 2, sm: 4, md: 5 },
          borderRadius: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            color: "#666",
            mb: 3,
          }}
        >
          Enter your new password below to reset your account password.
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          placeholder="New Password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange("password")}
          size="small"
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          placeholder="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={handleChange("confirmPassword")}
          size="small"
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              !formData.password.trim() ||
              !formData.confirmPassword.trim()
            }
            className={
              !loading &&
              formData.password.trim() &&
              formData.confirmPassword.trim()
                ? "next-button"
                : "next-button-disabled"
            }
            sx={{
              width: { xs: "100%", sm: 160 },
              height: 48,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography fontSize={{ xs: 13, sm: 14 }}>
            <Link
              component={RouterLink}
              to="/"
              variant="body2"
              underline="hover"
              sx={{
                color: "#263842",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to Sign In
            </Link>
          </Typography>
        </Box>
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ResetPassword;
